import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, Smartphone, Palette, BookOpen, Video, CheckCircle, Image as ImageIcon, Info, Loader2, PencilIcon, X } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { toast } from 'react-hot-toast';

/**
 * Helper function to normalize a domain
 */
const normalizeDomain = (url) => {
    if (!url) return '';
    
    let normalizedDomain = url.toLowerCase();
    
    if (normalizedDomain.startsWith('http')) {
        try {
            const urlObj = new URL(normalizedDomain);
            normalizedDomain = urlObj.hostname;
        } catch (e) {
            console.error('Error parsing URL:', e);
            normalizedDomain = normalizedDomain.replace(/^https?:\/\//, '').split('/')[0];
        }
    }
    
    return normalizedDomain.replace(/^www\./, '');
};

/**
 * DataLoader component for consistent loading indicators
 */
const DataLoader = ({ isLoading, children, fallback = null, showSpinner = true }) => {
    if (isLoading) {
        return (
            <div className="flex items-center space-x-2 text-white/70">
                {showSpinner && <Loader2 className="w-4 h-4 animate-spin" />}
                <span className="text-sm">Loading data...</span>
            </div>
        );
    }
    
    // If we have children, render them
    if (children) {
        return children;
    }
    
    // If we have a fallback, render it
    if (fallback) {
        return fallback;
    }
    
    // Default fallback
    return (
        <div className="flex items-center space-x-2 text-white/70">
            <span className="text-sm">No data available</span>
        </div>
    );
};

/**
 * Welcome slide for the App Demo wizard
 * Optimized layout for fixed-height container
 */
const WelcomeSlide = ({ registerValidation, organization }) => {
    const { 
        nextStep, 
        brandData, 
        selectedLogo, 
        primaryColor, 
        secondaryColor,
        fetchBrandData,
        brandDataFetched,
        setBrandData,
        setColorSchemes,
        setPrimaryColor,
        setSecondaryColor,
        setBrandDataFetched,
        fetchAttempts,
        fetchFailed,
        contentCategories,
        setContentCategories,
        appName,
        setAppName,
        appDescription,
        setAppDescription,
        appDemo
    } = useAppDemo();
    
    // State to control fade-in animations
    const [logoVisible, setLogoVisible] = useState(false);
    const [colorsVisible, setColorsVisible] = useState(false);
    const [showBrandInsight, setShowBrandInsight] = useState(false);
    const [isLoadingModal, setIsLoadingModal] = useState(false);
    
    // State to control selected app name suggestion
    const [selectedAppName, setSelectedAppName] = useState(appName || '');
    
    // State for content categories management
    const [checkedCategories, setCheckedCategories] = useState({});
    const [newCategory, setNewCategory] = useState('');
    const [displayedCategories, setDisplayedCategories] = useState([]);
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    
    // Create a ref for the input element
    const categoryInputRef = React.useRef(null);
    
    // Create a ref to track initial mount
    const initializedRef = React.useRef(false);
    
    // Add state for app name editing
    const [editingAppName, setEditingAppName] = useState(false);
    const [tempAppName, setTempAppName] = useState('');
    const appNameInputRef = useRef(null);
    
    // Initialize checked categories from context, preserving checked state
    useEffect(() => {
        console.log('Initializing categories for the first time');
        initializedRef.current = true;
        
        // Don't initialize with default categories if we're still loading
        // We'll show a loading state instead
        if (appDemo?.dataLoadingState?.brandData) {
            console.log('Brand data still loading, deferring category initialization');
            return;
        }
        
        if (contentCategories && contentCategories.length > 0) {
            const initialCheckedState = {};
            const categoryNames = [];
            
            // Process categories from context, checking if they have an isChecked property
            contentCategories.forEach(category => {
                const categoryName = typeof category === 'string' ? category : category.name;
                // If the category has an isChecked property, use it, otherwise default to true
                const isChecked = category.isChecked !== undefined ? category.isChecked : true;
                initialCheckedState[categoryName] = isChecked;
                categoryNames.push(categoryName);
            });
            
            setCheckedCategories(initialCheckedState);
            setDisplayedCategories(categoryNames);
        } else if (brandData && brandData.contentCategories && brandData.contentCategories.length > 0) {
            const initialCheckedState = {};
            brandData.contentCategories.forEach(category => {
                initialCheckedState[category.name] = true; // All categories initially checked
            });
            setCheckedCategories(initialCheckedState);
            
            // Set displayed categories from brand data
            setDisplayedCategories(brandData.contentCategories.map(cat => cat.name));
        } else {
            // No default categories - just empty state
            setCheckedCategories({});
            setDisplayedCategories([]);
        }
    }, [appDemo?.dataLoadingState?.brandData, brandData, contentCategories]);
    
    // Handle category checkbox change
    const handleCategoryToggle = (categoryName) => {
        console.log('Toggling category:', categoryName, 'Current state:', checkedCategories[categoryName]);
        
        // Create a new updated state with the toggled value
        const newCheckedState = { 
            ...checkedCategories, 
            [categoryName]: !checkedCategories[categoryName] 
        };
        
        console.log('New checked state:', newCheckedState);
        
        // Update local state first
        setCheckedCategories(newCheckedState);
        
        // Create a new array of categories with checked state for context
        const categoriesForContext = displayedCategories.map(name => ({
            name,
            score: 10,
            isChecked: newCheckedState[name] || false
        }));
        
        console.log('Updating context with:', categoriesForContext);
        
        // Directly update the context
        setContentCategories(categoriesForContext);
    };
    
    // Create a dedicated save function for adding categories
    const saveNewCategory = () => {
        console.log('Attempting to save category:', newCategory);
        
        if (!newCategory || newCategory.trim() === '') {
            console.log('Empty category, not saving');
            setIsAddingCategory(false);
            return;
        }
        
        const trimmedCategory = newCategory.trim();
        console.log('Trimmed category:', trimmedCategory);
        
        // Update displayed categories first
        const updatedDisplayedCategories = [...displayedCategories, trimmedCategory];
        console.log('New displayed categories:', updatedDisplayedCategories);
        setDisplayedCategories(updatedDisplayedCategories);
        
        // Update checked state ONLY for the new category
        const updatedCheckedState = { 
            ...checkedCategories, 
            [trimmedCategory]: true 
        };
        console.log('New checked state with added category:', updatedCheckedState);
        setCheckedCategories(updatedCheckedState);
        
        // Convert all categories including checked state for context update
        const allCategoriesWithState = updatedDisplayedCategories.map(name => ({
            name,
            score: 10,
            isChecked: updatedCheckedState[name] || false
        }));
        
        console.log('Final categories for context:', allCategoriesWithState);
        
        // Update context with complete set of categories and their checked state
        setContentCategories(allCategoriesWithState);
        
        // Reset states
        setNewCategory('');
        setIsAddingCategory(false);
    };
    
    // Handle adding new category
    const handleAddCategory = (categoryValue) => {
        if (!categoryValue || categoryValue.trim() === '') {
            setIsAddingCategory(false);
            return;
        }
        
        const trimmedCategory = categoryValue.trim();
        
        // Update displayed categories
        const newDisplayedCategories = [...displayedCategories, trimmedCategory];
        setDisplayedCategories(newDisplayedCategories);
        
        // Update checked status ONLY for new category
        const newCheckedState = { 
            ...checkedCategories, 
            [trimmedCategory]: true 
        };
        setCheckedCategories(newCheckedState);
        
        // Get all categories with checked state for context update
        const allCategoriesWithState = newDisplayedCategories.map(name => ({
            name,
            score: 10,
            isChecked: newCheckedState[name] || false
        }));
        
        // Update context with complete list
        setContentCategories(allCategoriesWithState);
        
        // Reset state
        setNewCategory('');
        setIsAddingCategory(false);
    };

    // Handle key press for inline category creation
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveNewCategory();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setNewCategory('');
            setIsAddingCategory(false);
        }
    };
    
    // Mark this slide as always valid - only run once on mount
    useEffect(() => {
        // Only call registerValidation if it exists and only once (empty dependency array)
        if (typeof registerValidation === 'function') {
            // A more robust way to register validation that won't cause infinite loops
            registerValidation('welcome', true);
        }
    // Explicitly add empty dependency array to ensure this only runs once on mount
    }, []);
    
    // Trigger animations when brand elements are loaded
    useEffect(() => {
        if (selectedLogo) {
            // Small delay for animation effect
            setTimeout(() => setLogoVisible(true), 300);
        }
        
        if (primaryColor || secondaryColor) {
            // Small delay for animation effect
            setTimeout(() => setColorsVisible(true), 500);
        }
    }, [selectedLogo, primaryColor, secondaryColor]);
    
    // Add fallback mechanism for when brand data is unavailable
    useEffect(() => {
        // After multiple failed attempts, create fallback brand data
        if ((fetchAttempts >= 3 || fetchFailed) && !brandData) {
            console.log('WelcomeSlide: Using fallback brand data due to server connection issues');
            
            // Create minimal fallback brand data
            const fallbackBrandData = {
                name: organization?.name || 'Your Organization',
                colors: [
                    { hex: '#1e6a5c', type: 'primary' },
                    { hex: '#6c2d82', type: 'secondary' }
                ],
                fonts: [],
                logos: [],
                domain: organization?.website_url
                    ? normalizeDomain(organization.website_url)
                    : 'yourorganization.com',
                screenshots: {
                    banner: []
                }
            };
            
            // Update context with fallback data
            setBrandData(fallbackBrandData);
            setColorSchemes([
                {
                    name: 'Default',
                    colors: ['#1e6a5c', '#6c2d82', '#ffffff']
                }
            ]);
            
            // Set primary and secondary colors
            setPrimaryColor('#1e6a5c');
            setSecondaryColor('#6c2d82');
            
            // Mark as fetched to prevent further attempts
            setBrandDataFetched(true);
        }
    }, [fetchAttempts, fetchFailed, brandData, organization, setBrandData, setColorSchemes, setPrimaryColor, setSecondaryColor, setBrandDataFetched]);

    // Add useEffect to set initial app name suggestion when brand data loads
    useEffect(() => {
        // Only set app name if there isn't one already in the context
        if (!appName && brandData) {
            // Try to get app names from different possible sources in brandData
            // Check for app suggestions first (matches the screenshot format)
            if (brandData.appSuggestions && brandData.appSuggestions.length > 0) {
                const firstSuggestion = brandData.appSuggestions[0];
                // Check for name_options array first
                if (firstSuggestion.name_options && firstSuggestion.name_options.length > 0) {
                    setSelectedAppName(firstSuggestion.name_options[0]);
                    setAppName(firstSuggestion.name_options[0]);
                } else if (firstSuggestion.name) {
                    // Fallback to name field
                    setSelectedAppName(firstSuggestion.name);
                    setAppName(firstSuggestion.name);
                }
            } 
            // Then check appIdeas as a fallback
            else if (brandData.appIdeas && brandData.appIdeas.length > 0) {
                const firstIdea = brandData.appIdeas[0];
                if (firstIdea.headline) {
                    setSelectedAppName(firstIdea.headline);
                    setAppName(firstIdea.headline);
                }
            } else if (organization?.name) {
                // Default to org name + App
                const defaultName = `${organization.name} App`;
                setSelectedAppName(defaultName);
                setAppName(defaultName);
            }
        }
    }, [brandData, organization, appName, setAppName]);

    // Force sync appName with context when WelcomeSlide mounts - SIMPLIFIED
    useEffect(() => {
        // Only sync from context to local if we have context data
        if (appName && !selectedAppName) {
            console.log('WelcomeSlide mount: Setting local state from context:', appName);
            setSelectedAppName(appName);
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    // Handle app name selection - SIMPLIFIED to avoid conflicts
    const handleAppNameSelect = (option) => {
        console.log('App name selected:', option.name);
        
        // Set both local and context state at the same time
        setSelectedAppName(option.name);
        setAppName(option.name);
        
        console.log('App name updated to:', option.name);
    };
    
    // Handle opening the brand insight modal
    const handleOpenBrandInsight = () => {
        setShowBrandInsight(true);
        setIsLoadingModal(true);
        
        // Simulate loading for better UX
        setTimeout(() => {
            setIsLoadingModal(false);
        }, 800);
    };
    
    // Render color swatches if we have brand colors
    const renderColorSwatches = () => {
        // Only render if we have primary or secondary color
        if (!primaryColor && !secondaryColor) return null;
        
        // Filter out default colors
        const hasValidPrimary = primaryColor && primaryColor !== "#1e9963" && primaryColor !== "#3b82f6";
        const hasValidSecondary = secondaryColor && secondaryColor !== "#1e9963" && secondaryColor !== "#3b82f6";
        
        if (!hasValidPrimary && !hasValidSecondary) {
            return (
                <div className="flex items-center justify-center gap-2 mt-3 mb-2">
                    <div className="text-xs text-white/70">No verified brand colors available</div>
                </div>
            );
        }
        
        return (
            <div className="flex items-center justify-center gap-2 mt-3 mb-2">
                {hasValidPrimary && (
                    <div 
                        className={`w-6 h-6 rounded-full border border-white/20 shadow-sm transition-opacity duration-500 ${colorsVisible ? 'opacity-100' : 'opacity-0'}`}
                        style={{ backgroundColor: primaryColor }}
                        title="Primary brand color"
                    />
                )}
                
                {hasValidSecondary && (
                    <div 
                        className={`w-6 h-6 rounded-full border border-white/20 shadow-sm transition-opacity duration-500 ${colorsVisible ? 'opacity-100' : 'opacity-0'}`}
                        style={{ backgroundColor: secondaryColor }}
                        title="Secondary brand color"
                    />
                )}
            </div>
        );
    };

    // Render full color palette from brand data
    const renderColorPalette = () => {
        if (!brandData || !brandData.colors || brandData.colors.length === 0) {
            // If no colors available, return color dots for primary/secondary if they exist
            return renderColorSwatches();
        }
        
        return (
            <div className="flex items-center gap-1.5">
                {brandData.colors.map((color, index) => (
                    <div 
                        key={`color-${index}`}
                        className="w-6 h-6 rounded-full border border-white/40 shadow-sm transition-opacity duration-300"
                        style={{ backgroundColor: color.hex }}
                        title={color.type || `Color ${index + 1}`}
                    />
                ))}
                <button 
                    onClick={handleOpenBrandInsight} 
                    className="ml-2 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
                    title="View detailed brand information"
                >
                    <Info className="w-4 h-4" />
                    <span>Brand Insight</span>
                </button>
            </div>
        );
    };

    // Get available app name options from the brand data
    const getAppNameOptions = () => {
        const options = [];
        
        // Get all name options from brand data first, preserving original order
        if (brandData?.appSuggestions && brandData.appSuggestions.length > 0) {
            const firstSuggestion = brandData.appSuggestions[0];
            if (firstSuggestion.name_options && firstSuggestion.name_options.length > 0) {
                firstSuggestion.name_options.forEach(name => {
                    options.push({
                        name: name,
                        description: firstSuggestion.description || '',
                        isSelected: name === selectedAppName,
                        isDefault: false
                    });
                });
            }
        }
        
        // If the selected app name is not in the options already, add it
        // Only do this if it's not a default placeholder name
        if (selectedAppName && 
            selectedAppName !== 'Your App' && 
            selectedAppName !== 'Enter App Name' && 
            !options.some(opt => opt.name === selectedAppName)) {
            
            // Insert at the beginning to make it visible
            options.unshift({
                name: selectedAppName,
                description: `Custom app name for ${organization?.name || 'your organization'}`,
                isSelected: true,
                isDefault: false
            });
        }
        
        // Only include a generic default option if no other options exist
        if (options.length === 0) {
            options.push({ 
                name: 'Enter App Name',
                description: 'Click the pencil icon to enter your app name',
                isSelected: false,
                isDefault: true
            });
        }
        
        return options;
    };

    // Handle removing an app name suggestion
    const handleRemoveSuggestion = (nameToRemove, e) => {
        // Prevent the click from bubbling up to the parent button
        e.stopPropagation();
        
        // Don't allow removing the selected name or default options
        if (nameToRemove === selectedAppName) return;
        
        // Create a new copy of brandData to modify
        const updatedBrandData = { ...brandData };
        
        // Remove from appSuggestions if it exists there
        if (updatedBrandData.appSuggestions) {
            updatedBrandData.appSuggestions = updatedBrandData.appSuggestions.map(suggestion => {
                if (suggestion.name_options) {
                    return {
                        ...suggestion,
                        name_options: suggestion.name_options.filter(name => name !== nameToRemove)
                    };
                }
                return suggestion;
            }).filter(suggestion => suggestion.name !== nameToRemove);
        }
        
        // Remove from appIdeas if it exists there
        if (updatedBrandData.appIdeas) {
            updatedBrandData.appIdeas = updatedBrandData.appIdeas.filter(idea => 
                idea.headline !== nameToRemove
            );
        }
        
        // Update the brandData in state
        setBrandData(updatedBrandData);
        
        // Force a re-render by updating a state value
        setAppName(prev => prev); // This triggers a re-render while keeping the same value
        
        // Show feedback to user
        toast.success(`Removed "${nameToRemove}" from suggestions`);
    };

    // Brand Insight Modal Component
    const BrandInsightModal = () => {
        if (!showBrandInsight) return null;
        
        // Check if we have actual brand data before rendering content
        const hasBrandData = brandData && typeof brandData === 'object';
        const orgName = organization?.name || (brandData?.name || 'Organization');
        const orgDomain = organization?.website_url?.replace(/^https?:\/\//, '').replace(/\/$/, '') || brandData?.domain || '';
        
        // Determine if we have real brand data or fallback data
        const isFallbackData = !brandData || 
            (brandData.colors && brandData.colors.length === 2 && 
             brandData.colors[0].hex === '#1e6a5c' && 
             brandData.colors[1].hex === '#6c2d82');
        
        return (
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Info className="w-5 h-5 text-blue-500" />
                            Brand Insight: {orgName}
                        </h3>
                        <button 
                            onClick={() => setShowBrandInsight(false)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path 
                                    fillRule="evenodd" 
                                    clipRule="evenodd" 
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                                />
                            </svg>
                        </button>
                    </div>
                    
                    <div className="p-6 overflow-y-auto flex-1">
                        {isLoadingModal ? (
                            <div className="text-center py-10">
                                <div className="text-gray-500 dark:text-gray-400 mb-4">
                                    <svg className="animate-spin w-10 h-10 mx-auto text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <p className="mt-4 text-lg">Loading brand data...</p>
                                </div>
                            </div>
                        ) : hasBrandData ? (
                            <div className="space-y-6">
                                {/* Brand Overview */}
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Overview</h4>
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="md:w-1/3">
                                            {brandData.logos && brandData.logos.length > 0 ? (
                                                <div className="space-y-3">
                                                    {brandData.logos.map((logo, logoIndex) => (
                                                        <div key={`logo-${logoIndex}`} className={`p-3 rounded-lg shadow-sm flex items-center justify-center h-32 ${logo.theme === 'light' ? 'bg-white' : logo.theme === 'dark' ? 'bg-gray-800' : 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-900'}`}>
                                                            {logo.formats && logo.formats.length > 0 ? (
                                                                <img 
                                                                    src={logo.formats[0].src} 
                                                                    alt={`${brandData.name || orgName} logo (${logo.type})`}
                                                                    className="max-h-24 max-w-full object-contain" 
                                                                    onError={(e) => {
                                                                        console.error("Logo load error:", e);
                                                                        e.target.onerror = null;
                                                                        e.target.src = logo.formats.length > 1 ? logo.formats[1].src : '';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="text-sm text-gray-500">No logo format available</div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : selectedLogo?.src ? (
                                                <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-center h-32">
                                                    <img 
                                                        src={selectedLogo.src} 
                                                        alt={`${orgName} logo`}
                                                        className="max-h-24 max-w-full object-contain" 
                                                    />
                                                </div>
                                            ) : (
                                                <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-center h-32">
                                                    <span className="text-gray-400">No logo available</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="md:w-2/3">
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{brandData.name || orgName}</h3>
                                            <div className="text-xs text-gray-500 mb-1">
                                                {JSON.stringify({hasDescription: !!brandData.description})}
                                            </div>
                                            <p className="text-gray-700 dark:text-gray-300 my-2">{brandData.description || 'No description available.'}</p>
                                            {orgDomain && (
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="font-medium">Domain:</span> {orgDomain}
                                                </div>
                                            )}
                                            {isFallbackData && (
                                                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm rounded-md">
                                                    <p>⚠️ Using fallback brand data due to server connection issues. The colors shown are placeholders.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Brand Colors */}
                                {brandData.colors && brandData.colors.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Brand Colors</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {brandData.colors
                                                .filter(color => !isFallbackData || (color.hex !== "#1e9963" && color.hex !== "#3b82f6")) // Filter out default colors
                                                .map((color, index) => (
                                                <div key={`color-${index}`} className="flex flex-col items-center">
                                                    <div 
                                                        className="w-12 h-12 rounded-md border shadow-sm mb-2" 
                                                        style={{ backgroundColor: color.hex }}
                                                    />
                                                    <div className="text-xs text-center">
                                                        <div className="font-semibold text-gray-900 dark:text-white">{color.type || 'Color'}</div>
                                                        <div className="text-gray-600 dark:text-gray-400">{color.hex}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        {/* Note about Material Design Theme */}
                                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                            <div className="flex items-start">
                                                <Info className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" />
                                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                                    Proceed to the next step to explore Material Design color palettes generated from your brand colors.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {brandData.colors.filter(color => !isFallbackData && (color.hex !== "#1e9963" && color.hex !== "#3b82f6")).length === 0 && !isFallbackData && (
                                            <p className="text-gray-600 dark:text-gray-400">No verified brand colors available.</p>
                                        )}
                                    </div>
                                )}

                                {/* Fonts */}
                                {brandData.fonts && brandData.fonts.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Typography</h4>
                                        <div className="space-y-3">
                                            {brandData.fonts.map((font, index) => (
                                                <div key={`font-${index}`} className="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                                                    <div className="font-medium text-gray-900 dark:text-white">{font.name}</div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">{font.type}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Social Links */}
                                {brandData.links && brandData.links.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Social Media</h4>
                                        <div className="space-y-2">
                                            {brandData.links.map((link, index) => {
                                                // Extract domain from URL
                                                let domain = '';
                                                try {
                                                    const url = new URL(link.url);
                                                    domain = url.hostname.replace('www.', '');
                                                } catch (e) {
                                                    domain = link.url;
                                                }
                                                
                                                return (
                                                    <a 
                                                        key={`social-${index}`}
                                                        href={link.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="flex items-center p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                    >
                                                        <div className="text-gray-900 dark:text-white">{domain}</div>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                        </svg>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Screenshots/Images */}
                                {brandData.screenshots?.banner && brandData.screenshots.banner.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Banner</h4>
                                        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                                            <img 
                                                src={brandData.screenshots.banner[0].url} 
                                                alt="Brand banner"
                                                className="w-full object-contain" 
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* App Ideas */}
                                {brandData.appIdeas && brandData.appIdeas.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">App Ideas</h4>
                                        <div className="space-y-3">
                                            {brandData.appIdeas.map((idea, index) => (
                                                <div key={`idea-${index}`} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white mb-1">{idea.headline}</h5>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300">{idea.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Features */}
                                {brandData.features && brandData.features.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Recommended Features</h4>
                                        <div className="space-y-3">
                                            {brandData.features.map((feature, index) => (
                                                <div key={`feature-${index}`} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center">
                                                    <div className="flex-1">
                                                        <h5 className="font-medium text-gray-900 dark:text-white">{feature.name}</h5>
                                                        <p className="text-sm text-gray-700 dark:text-gray-300">{feature.description}</p>
                                                    </div>
                                                    <div className={`ml-2 px-2 py-1 text-xs font-medium rounded ${
                                                        feature.priority === 'high' 
                                                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
                                                            : feature.priority === 'medium'
                                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                                    }`}>
                                                        {feature.priority}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Marketing Tips */}
                                {brandData.marketingTips && brandData.marketingTips.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Marketing Tips</h4>
                                        <div className="space-y-3">
                                            {brandData.marketingTips.map((tip, index) => (
                                                <div key={`tip-${index}`} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">{tip.category}</div>
                                                    <p className="text-gray-700 dark:text-gray-300">{tip.text}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Content Categories */}
                                {brandData.contentCategories && brandData.contentCategories.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Content Categories</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {brandData.contentCategories.map((category, index) => (
                                                <div key={`category-${index}`} className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className="flex justify-between items-center">
                                                        <h5 className="font-medium text-gray-900 dark:text-white">{category.name}</h5>
                                                        <div className="bg-gray-100 dark:bg-gray-700 text-xs px-2 py-1 rounded-full">
                                                            Score: {category.score}/10
                                                        </div>
                                                    </div>
                                                    {category.description && (
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{category.description}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Brand Analysis */}
                                {brandData.brandAnalysis && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Brand Analysis</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Industry Category</h5>
                                                <p className="text-gray-700 dark:text-gray-300">
                                                    {brandData.brandAnalysis.industry_category || 'Not specified'}
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Target Audience</h5>
                                                <p className="text-gray-700 dark:text-gray-300">
                                                    {brandData.brandAnalysis.target_audience || 'Not specified'}
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Brand Voice</h5>
                                                <p className="text-gray-700 dark:text-gray-300">
                                                    {brandData.brandAnalysis.brand_voice || 'Not specified'}
                                                </p>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Key Value Propositions</h5>
                                                <p className="text-gray-700 dark:text-gray-300">
                                                    {brandData.brandAnalysis.key_value_propositions || 'Not specified'}
                                                </p>
                                            </div>
                                            {brandData.brandAnalysis.founded_year > 0 && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Founded Year</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.founded_year}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.employees > 0 && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Employees</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.employees.toLocaleString()}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.revenue && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Revenue</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.revenue}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.location && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Location</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.location}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.competitive_position && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Competitive Position</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.competitive_position}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.brand_challenges && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Brand Challenges</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.brand_challenges}
                                                    </p>
                                                </div>
                                            )}
                                            {brandData.brandAnalysis.mission_statement && (
                                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 col-span-1 md:col-span-2">
                                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">Mission Statement</h5>
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {brandData.brandAnalysis.mission_statement}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Technical Requirements */}
                                {brandData.technicalRequirements && brandData.technicalRequirements.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Technical Requirements</h4>
                                        <ul className="list-disc pl-5 space-y-1">
                                            {brandData.technicalRequirements.map((req, index) => (
                                                <li key={`req-${index}`} className="text-gray-700 dark:text-gray-300">
                                                    {typeof req === 'string' ? req : (req.description || req.text || JSON.stringify(req))}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* App Suggestions - UPDATED to show all fields including monetization */}
                                {brandData.appSuggestions && brandData.appSuggestions.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">App Suggestions</h4>
                                        <div className="space-y-4">
                                            {brandData.appSuggestions.map((suggestion, index) => (
                                                <div key={`app-suggestion-${index}`} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            {suggestion.name_options && suggestion.name_options.length > 0 ? (
                                                                <h5 className="font-medium text-gray-900 dark:text-white text-lg">{suggestion.name_options[0]}</h5>
                                                            ) : (
                                                                <h5 className="font-medium text-gray-900 dark:text-white text-lg">{suggestion.name || "App Suggestion"}</h5>
                                                            )}
                                                            
                                                            {suggestion.name_options && suggestion.name_options.length > 1 && (
                                                                <div className="mt-1 flex flex-wrap gap-1">
                                                                    {suggestion.name_options.slice(1).map((name, nameIndex) => (
                                                                        <span key={`name-${nameIndex}`} className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                                                                            {name}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                                                            {suggestion.app_type || "Mobile App"}
                                                        </span>
                                                    </div>
                                                    
                                                    <p className="text-gray-700 dark:text-gray-300 my-2">{suggestion.description}</p>
                                                    
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                                        {suggestion.target_audience && (
                                                            <div className="text-sm">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Target Audience: </span>
                                                                <span className="text-gray-600 dark:text-gray-400">{suggestion.target_audience}</span>
                                                            </div>
                                                        )}
                                                        {suggestion.monetization_strategy && (
                                                            <div className="text-sm">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Monetization Strategy: </span>
                                                                <span className="text-gray-600 dark:text-gray-400">{suggestion.monetization_strategy}</span>
                                                            </div>
                                                        )}
                                                        {suggestion.development_time && (
                                                            <div className="text-sm">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Development Time: </span>
                                                                <span className="text-gray-600 dark:text-gray-400">{suggestion.development_time}</span>
                                                            </div>
                                                        )}
                                                        {suggestion.value_proposition && (
                                                            <div className="text-sm col-span-2">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">Value Proposition: </span>
                                                                <span className="text-gray-600 dark:text-gray-400">{suggestion.value_proposition}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Fallback section when only basic brand data is available */}
                                {!brandData.colors && !brandData.fonts && !brandData.links && !brandData.screenshots && (
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                        <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Organization Details</h4>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            Basic brand information is available for {orgName}.
                                            {primaryColor && primaryColor !== "#1e9963" && primaryColor !== "#3b82f6" && (
                                                <span> The primary brand color is <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: primaryColor, verticalAlign: 'middle' }}></span> {primaryColor}.</span>
                                            )}
                                            {secondaryColor && secondaryColor !== "#1e9963" && secondaryColor !== "#3b82f6" && (
                                                <span> The secondary brand color is <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: secondaryColor, verticalAlign: 'middle' }}></span> {secondaryColor}.</span>
                                            )}
                                            {(!primaryColor || primaryColor === "#1e9963" || primaryColor === "#3b82f6") && 
                                             (!secondaryColor || secondaryColor === "#1e9963" || secondaryColor === "#3b82f6") && (
                                                <span> No verified brand colors are available yet.</span>
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Basic Organization Info when no brand data */}
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <h4 className="font-medium text-lg mb-3 text-gray-900 dark:text-white">Organization Information</h4>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{orgName}</h3>
                                            {orgDomain && (
                                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                    <span className="font-medium">Website:</span> {orgDomain}
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm rounded-md">
                                            <p>⚠️ Unable to fetch brand data from the server. Please check your connection and try again later.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end">
                        <button 
                            onClick={() => setShowBrandInsight(false)}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Focus input when isAddingCategory becomes true
    useEffect(() => {
        if (isAddingCategory && categoryInputRef.current) {
            categoryInputRef.current.focus();
        }
    }, [isAddingCategory]);

    // Handle app name edit start
    const handleStartEditAppName = () => {
        setTempAppName(appName || '');
        setEditingAppName(true);
        // Focus the input after it renders
        setTimeout(() => {
            if (appNameInputRef.current) {
                appNameInputRef.current.focus();
            }
        }, 10);
    };

    // Handle app name edit save
    const handleSaveAppName = () => {
        if (tempAppName && tempAppName.trim() !== '') {
            const newName = tempAppName.trim();
            setAppName(newName);
            setSelectedAppName(newName);
            
            // Add the custom name to the app name options if it's not already there
            if (brandData && brandData.appSuggestions && brandData.appSuggestions.length > 0) {
                // Create a deep copy of brand data to modify
                const updatedBrandData = JSON.parse(JSON.stringify(brandData));
                
                // Find the first suggestion with name_options
                const firstSuggestion = updatedBrandData.appSuggestions[0];
                
                // If name_options exists, add the new name to it if not already there
                if (firstSuggestion.name_options) {
                    // Check if the name already exists in the options
                    if (!firstSuggestion.name_options.includes(newName)) {
                        // Add the new name to the beginning of the array
                        firstSuggestion.name_options.unshift(newName);
                        console.log(`Added "${newName}" to name_options`);
                    }
                } else {
                    // If name_options doesn't exist, create it with the new name
                    firstSuggestion.name_options = [newName];
                    console.log(`Created name_options with "${newName}"`);
                }
                
                // Update the brand data in state
                setBrandData(updatedBrandData);
                toast.success(`Added "${newName}" to app name options`);
            } else {
                // If there are no app suggestions yet, create a basic structure
                if (!brandData) {
                    setBrandData({
                        appSuggestions: [
                            {
                                name: 'Custom App',
                                description: 'Custom app name suggestions',
                                name_options: [newName]
                            }
                        ]
                    });
                } else {
                    // Create a deep copy
                    const updatedBrandData = JSON.parse(JSON.stringify(brandData));
                    
                    // Ensure appSuggestions exists
                    updatedBrandData.appSuggestions = updatedBrandData.appSuggestions || [];
                    
                    // Add a new suggestion with the custom name
                    updatedBrandData.appSuggestions.unshift({
                        name: 'Custom App',
                        description: 'Custom app name suggestions',
                        name_options: [newName]
                    });
                    
                    // Update brand data
                    setBrandData(updatedBrandData);
                }
                toast.success(`Created app name options with "${newName}"`);
            }
        } else {
            // If empty, set a default
            setAppName('MyApp');
            setSelectedAppName('MyApp');
        }
        setEditingAppName(false);
    };

    // Handle app name edit cancel
    const handleCancelAppName = () => {
        setEditingAppName(false);
    };

    // Handle app name input keydown
    const handleAppNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSaveAppName();
        } else if (e.key === 'Escape') {
            handleCancelAppName();
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header with Logo, Intro, and Content Categories */}
            <div className="mb-3">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                    {selectedLogo?.src ? (
                            <div className={`mr-4 bg-white/10 backdrop-blur-sm p-2 rounded-lg transition-opacity duration-500 ${logoVisible ? 'opacity-100' : 'opacity-0'}`}>
                            <img 
                                src={selectedLogo.src} 
                                alt="Organization logo" 
                                    className="h-12 max-w-[150px] object-contain"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    ) : (
                            <div className="mr-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Sparkles className="w-6 h-6" />
                        </div>
                    )}
                        
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-0.5">
                    Welcome to the App Builder
                </h2>
                            <p className="text-base text-white/90">
                                Let's create a custom branded book & learning app for{' '}
                                <DataLoader 
                                    isLoading={appDemo?.dataLoadingState?.appName} 
                                    fallback={<span className="text-white/60 italic">Your Organization</span>}
                                >
                                    {appName || organization?.name || 'your organization'}
                                </DataLoader>
                            </p>
                        </div>
                    </div>
                    
                    {/* Color Palette */}
                    <div className="flex flex-col items-end">
                        <div className={`transition-opacity duration-700 ${colorsVisible ? 'opacity-100' : 'opacity-0'}`}>
                            {renderColorPalette()}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Content Categories and App Name Selection in a horizontal layout */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Content Categories */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-2">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="text-base font-semibold text-yellow-300 flex items-center">
                            <BookOpen className="w-4 h-4 mr-1" />
                            Content Categories
                        </h3>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto">
                        <DataLoader isLoading={appDemo?.dataLoadingState?.brandData}>
                            {/* Display existing categories */}
                            {displayedCategories.length > 0 ? (
                                displayedCategories.map((category, index) => (
                                    <div key={index} className={`flex items-center bg-white/10 rounded-md px-3 py-1 ${checkedCategories[category] ? 'border border-yellow-300/50' : 'border border-white/20'}`}>
                                        <input 
                                            type="checkbox" 
                                            id={`category-${index}`}
                                            checked={checkedCategories[category] || false}
                                            onChange={() => handleCategoryToggle(category)}
                                            className="mr-2"
                                        />
                                        <label htmlFor={`category-${index}`} className="text-sm text-white font-medium cursor-pointer">
                                            {category}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <div className="w-full text-center py-2">
                                    <span className="text-white/60 text-sm">No categories defined yet</span>
                                </div>
                            )}
                        </DataLoader>
                        
                        {/* Inline add category placeholder */}
                        {isAddingCategory ? (
                            <div className="flex items-center bg-white/10 rounded-md px-3 py-1 border border-yellow-300 min-w-[120px]">
                                <input 
                                    ref={categoryInputRef}
                                    type="text"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    onBlur={() => {
                                        console.log('Input blurred, saving category');
                                        // Short delay to ensure the value is captured
                                        setTimeout(() => saveNewCategory(), 100);
                                    }}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Type category..."
                                    className="w-full bg-transparent text-sm text-white focus:outline-none"
                                />
                            </div>
                        ) : (
                            <div 
                                onClick={() => setIsAddingCategory(true)}
                                className="flex items-center bg-white/5 rounded-md px-3 py-1 border border-dashed border-white/30 cursor-pointer hover:bg-white/10 hover:border-yellow-300/50 transition-colors"
                            >
                                <span className="text-sm text-white/70 font-medium">+ Add category</span>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* App Name Selection - with improved inline editing */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-2">
                    <div className="flex justify-between items-center mb-1">
                        <h3 className="text-base font-semibold text-yellow-300 flex items-center">
                            <Smartphone className="w-4 h-4 mr-1" />
                            Choose Your App Name
                        </h3>
                        <div className="text-base text-white font-medium">
                            <DataLoader isLoading={appDemo?.dataLoadingState?.appName} showSpinner={false}>
                                {selectedAppName}
                            </DataLoader>
                        </div>
                    </div>
                            
                    {/* App name options with inline editing */}
                    <div className="flex flex-wrap gap-2">
                        <DataLoader isLoading={appDemo?.dataLoadingState?.appName}>
                            {getAppNameOptions().map((option, index) => (
                                <div 
                                    key={index}
                                    className="relative group"
                                >
                                    <button 
                                        onClick={() => handleAppNameSelect(option)}
                                        className={`px-3 py-1 text-sm rounded-md flex items-center gap-1 ${
                                            option.isSelected
                                                ? 'bg-green-600 text-white' 
                                                : 'bg-white/10 text-white hover:bg-white/20'
                                        }`}
                                    >
                                        {option.isSelected && <CheckCircle className="w-3 h-3" />}
                                        {option.name}
                                    </button>
                                    {!option.isSelected && !option.isDefault && (
                                        <button
                                            onClick={(e) => handleRemoveSuggestion(option.name, e)}
                                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                                            title="Remove"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            
                            {/* Inline editor for adding new app name */}
                            {editingAppName ? (
                                <div className="relative">
                                    <input
                                        ref={appNameInputRef}
                                        type="text"
                                        value={tempAppName}
                                        onChange={(e) => setTempAppName(e.target.value)}
                                        onKeyDown={handleAppNameKeyDown}
                                        placeholder="Enter app name..."
                                        className="px-3 py-1 text-sm border border-yellow-300/50 bg-black/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-300/50 min-w-[120px]"
                                        autoFocus
                                    />
                                    <div className="absolute top-full left-0 mt-1 flex space-x-1 z-10">
                                        <button
                                            onClick={handleSaveAppName}
                                            className="text-xs px-2 py-1 bg-green-600 text-white rounded"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={handleCancelAppName}
                                            className="text-xs px-2 py-1 bg-gray-600 text-white rounded"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={handleStartEditAppName}
                                    className="px-3 py-1 text-sm rounded-md flex items-center gap-1 bg-white/10 text-white hover:bg-white/20"
                                >
                                    <PencilIcon className="w-3 h-3 mr-1" />
                                    Add Custom
                                </button>
                            )}
                        </DataLoader>
                    </div>
                </div>
            </div>

            {/* Main Content - Two Column Layout with adjusted proportions */}
            <div className="flex-1 grid grid-cols-5 gap-4 overflow-hidden">
                {/* Left Column: Brand Profile - narrower (2/5) */}
                <div className="col-span-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-3 flex flex-col overflow-hidden">
                    <h3 className="text-base font-semibold text-yellow-300 mb-2 flex items-center">
                        <Palette className="w-4 h-4 mr-1" />
                        Brand Profile
                    </h3>
                    <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                        {brandData && brandData.name && (
                            <div>
                                <span className="text-sm text-white/70 block mb-0.5">Organization:</span>
                                <p className="text-base text-white font-medium">{brandData.name}</p>
                            </div>
                        )}
                        
                        {organization && organization.website_url && (
                            <div>
                                <span className="text-sm text-white/70 block mb-0.5">Website:</span>
                                <p className="text-sm text-white">{organization.website_url}</p>
                            </div>
                        )}
                        
                        {brandData && brandData.brandAnalysis && brandData.brandAnalysis.industry_category && (
                            <div>
                                <span className="text-sm text-white/70 block mb-0.5">Industry:</span>
                                <p className="text-sm text-white">{brandData.brandAnalysis.industry_category}</p>
                            </div>
                        )}
                        
                        {brandData && brandData.brandAnalysis && brandData.brandAnalysis.target_audience && (
                            <div>
                                <span className="text-sm text-white/70 block mb-0.5">Target Audience:</span>
                                <p className="text-sm text-white">{brandData.brandAnalysis.target_audience}</p>
                            </div>
                        )}
                        
                        {brandData && brandData.brandAnalysis && brandData.brandAnalysis.brand_voice && (
                            <div>
                                <span className="text-sm text-white/70 block mb-0.5">Brand Voice:</span>
                                <p className="text-sm text-white">{brandData.brandAnalysis.brand_voice}</p>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Right Column: App Preview and Potential - wider (3/5) */}
                <div className="col-span-3 flex flex-col gap-3">
                    {/* App Preview and App Potential - Equal height panels */}
                    <div className="flex-1 grid grid-cols-2 gap-3">
                        {/* Phone Mockup */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-3 flex flex-col">
                            <h3 className="text-base font-semibold text-yellow-300 mb-2 flex items-center">
                                <Smartphone className="w-4 h-4 mr-1" />
                                App Preview
                            </h3>
                            <div className="flex-1 flex justify-center items-center">
                                <div className="relative w-[220px] h-[400px] rounded-[24px] bg-gray-800 p-2 shadow-lg">
                                    {/* Screen */}
                                    <div 
                                        className="w-full h-full rounded-[20px] flex flex-col overflow-hidden" 
                                        style={{ backgroundColor: primaryColor || '#1e6a5c' }}
                                    >
                                        {/* App Header */}
                                        <div className="p-2 flex items-center justify-between" style={{ backgroundColor: secondaryColor || '#6c2d82' }}>
                                            <div className="flex items-center">
                                                {selectedLogo?.src ? (
                                                    <div className="w-6 h-6 rounded-md bg-white/90 flex items-center justify-center p-0.5">
                                                        <img 
                                                            src={selectedLogo.src} 
                                                            alt="App logo" 
                                                            className="max-w-full max-h-full object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-6 h-6 rounded-md bg-white/90 flex items-center justify-center">
                                                        <span className="text-[10px] font-bold" style={{ color: primaryColor }}>
                                                            {organization?.name?.substring(0, 2).toUpperCase() || 'AP'}
                                                        </span>
                                                    </div>
                                                )}
                                                <span className="ml-1.5 text-white font-medium text-xs truncate max-w-[120px]">
                                                    {selectedAppName || (organization?.name ? `${organization.name} App` : 'Your App')}
                                                </span>
                                            </div>
                                            <div className="w-4 h-4 rounded-full bg-white/20"></div>
                                        </div>
                                        
                                        {/* Main Content Area */}
                                        <div className="flex-1 p-3">
                                            {/* Banner Image */}
                                            {brandData?.screenshots?.banner && brandData.screenshots.banner.length > 0 ? (
                                                <div className="bg-white/10 rounded-lg overflow-hidden h-[80px] mb-3">
                                                    <img 
                                                        src={brandData.screenshots.banner[0].url} 
                                                        alt="Banner"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="bg-white/10 rounded-lg h-[80px] mb-3"></div>
                                            )}
                                            
                                            {/* Content Preview */}
                                            <div className="space-y-3">
                                                <div className="bg-white/10 rounded-lg h-8 mb-3"></div>
                                                <div className="flex gap-2 mb-3">
                                                    <div className="w-12 h-16 bg-white/10 rounded-lg"></div>
                                                    <div className="w-12 h-16 bg-white/10 rounded-lg"></div>
                                                    <div className="w-12 h-16 bg-white/10 rounded-lg"></div>
                                                </div>
                                                <div className="bg-white/10 rounded-lg h-8 mt-3"></div>
                                                <div className="bg-white/10 rounded-lg h-20 mt-3"></div>
                                            </div>
                                        </div>
                                        
                                        {/* Bottom Navigation */}
                                        <div className="bg-black/20 p-2 flex justify-around">
                                            <div className="w-6 h-6 rounded-full bg-white/10"></div>
                                            <div className="w-6 h-6 rounded-full bg-white/10"></div>
                                            <div className="w-6 h-6 rounded-full bg-white/10"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* App Potential - Better shaped panel */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-3 flex flex-col">
                            <h3 className="text-base font-semibold text-yellow-300 mb-2 flex items-center">
                                <Sparkles className="w-4 h-4 mr-1" />
                                App Potential
                            </h3>
                            
                            {/* App description first */}
                            <div className="bg-white/5 rounded-lg p-3 mb-3">
                                {getAppNameOptions().find(option => option.name === selectedAppName)?.description ? (
                                    <p className="text-sm text-white">
                                        {getAppNameOptions().find(option => option.name === selectedAppName)?.description}
                                    </p>
                                ) : (
                                    <p className="text-sm text-white/70">
                                        A custom branded application for {organization?.name || 'your organization'}.
                                    </p>
                                )}
                            </div>
                            
                            {/* App suggestions/ideas */}
                            <div className="flex-1">
                                <h4 className="text-sm text-white/70 mb-2">Features:</h4>
                                {brandData && brandData.appSuggestions && brandData.appSuggestions.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {brandData.appSuggestions.map((suggestion, index) => (
                                            <React.Fragment key={`suggestion-feature-${index}`}>
                                                {suggestion.target_audience && (
                                                    <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                                        {suggestion.target_audience}
                                                    </div>
                                                )}
                                                {suggestion.monetization_strategy && (
                                                    <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                                        {suggestion.monetization_strategy}
                                                    </div>
                                                )}
                                                {suggestion.app_type && (
                                                    <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                                        {suggestion.app_type}
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Subscription Access
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Content Library
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Premium Features
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Mobile App
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Subscription Access
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Content Library
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Premium Features
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-white/10 rounded-full text-white/80">
                                            Mobile App
                                        </div>
                                    </div>
                                )}
                                
                                <h4 className="text-sm text-white/70 mb-2">Target Audience:</h4>
                                {brandData && brandData.brandAnalysis && brandData.brandAnalysis.target_audience ? (
                                    <p className="text-sm text-white/80 mb-3">{brandData.brandAnalysis.target_audience}</p>
                                ) : (
                                    <p className="text-sm text-white/80 mb-3">Readers interested in religious and spiritual content.</p>
                                )}
                                
                                <h4 className="text-sm text-white/70 mb-2">Monetization:</h4>
                                <p className="text-sm text-white/80">Subscription-based access to premium content and exclusive features.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Brand Insight Modal */}
            <BrandInsightModal />
        </div>
    );
};

export default WelcomeSlide; 